import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useWakeLock } from '../platform/device'
import { ITEM_SHORT, itemState, nextOpenIndex, type ItemState } from '../sync/checklistNav'
import { getPhoto, photoKey } from '../sync/db'
import { saveDraft, savePhotoLocal, type StopDraft } from '../sync/drafts'
import { compressPhoto } from '../sync/photo'
import type { DriverStop } from '../sync/route'
import { CONDITIONS, ITEM_TITLES, TAP, bigButton, button, color, container, footer, h1, muted, page, textButton } from '../ui'

/**
 * CHECKLIST (v3 section 9): one item per screen. Tapping the item OPENS THE
 * CAMERA IMMEDIATELY. Shoot; for a condition item the condition buttons
 * appear as large tap targets; tap one and it moves on. Every item is
 * skippable. Every tap is written to IndexedDB as it happens.
 *
 * The chip row at the top moves freely between items (Brad, field test
 * 2026-09-14): green = photo or condition, dash = skipped, outlined = the one
 * on screen. Answering an item moves to the next one still open, wrapping
 * round; when none is open, the stop goes to Finish. Finish is always a chip.
 */
export function ChecklistScreen({
  stop,
  draft,
  onDraftChange,
  onDone,
  onBack,
}: {
  stop: DriverStop
  draft: StopDraft
  onDraftChange: (d: StopDraft) => void
  onDone: () => void
  onBack: () => void
}) {
  const items = stop.checklist_required
  const index = Math.min(draft.index, items.length - 1)
  const itemType = items[index]
  const item = draft.items[itemType] ?? { condition: '', skipped: false, captured_at: null, hasPhoto: false }
  const conditions = CONDITIONS[itemType]
  const fileRef = useRef<HTMLInputElement>(null)
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [thumb, setThumb] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  useWakeLock(true)

  useEffect(() => {
    let url = ''
    ;(async () => {
      const p = await getPhoto(photoKey(stop.uuid, itemType))
      if (p?.blob) {
        url = URL.createObjectURL(p.blob)
        setThumb(url)
      } else {
        setThumb('')
      }
    })()
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [stop.uuid, itemType, item.captured_at])

  useEffect(() => {
    chipRefs.current[index]?.scrollIntoView?.({ block: 'nearest', inline: 'center' })
  }, [index])

  const persist = async (next: StopDraft) => {
    await saveDraft(next)
    onDraftChange(next)
  }

  const advance = async (next: StopDraft) => {
    const target = nextOpenIndex(items, next.items, index)
    if (target == null) {
      await persist({ ...next, index: items.length })
      onDone()
    } else {
      await persist({ ...next, index: target })
    }
  }

  const goTo = async (i: number) => {
    setErr('')
    if (i !== index) await persist({ ...draft, index: i })
  }

  const onPhoto = async (file: File | undefined) => {
    if (!file) return
    setBusy(true)
    setErr('')
    try {
      const capturedAt = new Date().toISOString()
      const c = await compressPhoto(file)
      await savePhotoLocal(stop.uuid, itemType, c.blob, capturedAt)
      const next: StopDraft = {
        ...draft,
        items: { ...draft.items, [itemType]: { ...item, hasPhoto: true, skipped: false, captured_at: capturedAt } },
      }
      if (conditions) {
        await persist(next) // stay here: the condition buttons appear
      } else {
        await advance(next) // photo-only item: on to the next open one
      }
    } catch (e) {
      setErr(`Photo failed (${e instanceof Error ? e.message : String(e)}). Try again, or skip.`)
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const setCondition = async (value: string) => {
    await advance({
      ...draft,
      items: { ...draft.items, [itemType]: { ...item, condition: value, skipped: false } },
    })
  }

  // "Skip" on an item that already has a photo or a condition (a fix) means
  // "leave it as it is and move on", not "throw it away".
  const skipItem = async () => {
    const keep = item.hasPhoto || !!item.condition
    await advance({
      ...draft,
      items: { ...draft.items, [itemType]: { ...item, skipped: keep ? item.skipped : true } },
    })
  }

  const chip = (state: ItemState, current: boolean): CSSProperties => ({
    flex: '0 0 auto',
    minHeight: TAP,
    padding: '0 16px',
    borderRadius: 999,
    fontSize: 17,
    fontWeight: 800,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    background: state === 'done' ? color.green : color.white,
    color: state === 'done' ? color.white : state === 'skipped' ? color.grey : color.ink,
    border: current ? `3px solid ${color.ink}` : `2px solid ${state === 'done' ? color.green : color.greyLight}`,
  })

  return (
    <div style={page}>
      <div style={container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button style={{ ...textButton, paddingLeft: 0 }} onClick={onBack}>‹ Stop</button>
          <span style={{ ...muted, fontWeight: 700 }}>{index + 1} of {items.length}</span>
        </div>

        <div
          role="tablist"
          aria-label="Checklist items"
          style={{ display: 'flex', gap: 8, overflowX: 'auto', margin: '0 -16px 8px', padding: '4px 16px 10px' }}
        >
          {items.map((t, i) => {
            const state = itemState(draft.items[t])
            return (
              <button
                key={t}
                ref={(el) => {
                  chipRefs.current[i] = el
                }}
                role="tab"
                aria-selected={i === index}
                style={chip(state, i === index)}
                onClick={() => goTo(i)}
              >
                {state === 'done' ? '✓ ' : state === 'skipped' ? '– ' : ''}
                {ITEM_SHORT[t] ?? t}
              </button>
            )
          })}
          <button style={{ ...chip('todo', false), background: color.ink, color: color.white, borderColor: color.ink }} onClick={onDone}>
            Finish ▸
          </button>
        </div>

        <div style={muted}>{stop.name}</div>
        <div style={h1}>{ITEM_TITLES[itemType] ?? itemType}</div>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => onPhoto(e.target.files?.[0])}
        />

        {thumb ? (
          <img src={thumb} alt="" style={{ width: '100%', borderRadius: 14, marginBottom: 12, maxHeight: '38vh', objectFit: 'cover' }} />
        ) : (
          <div style={{ height: 120, borderRadius: 14, background: color.greyLight, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: color.grey, fontSize: 18 }}>
            No photo yet
          </div>
        )}

        <button style={bigButton(item.hasPhoto ? color.grey : color.green, busy)} disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? 'Saving photo…' : item.hasPhoto ? 'Retake photo' : '📷  Take photo'}
        </button>
        {err && <div style={{ color: color.redDark, fontWeight: 700, marginTop: 8 }}>{err}</div>}

        {conditions && (
          <div style={{ marginTop: 16 }}>
            <div style={{ ...muted, marginBottom: 8 }}>{item.hasPhoto ? 'Now tap the condition:' : 'Condition (photo first, or tap to skip the photo):'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {conditions.map((c) => (
                <button
                  key={c.value}
                  style={{ ...button(item.condition === c.value ? color.greenDark : color.white, item.condition === c.value ? color.white : color.ink), minHeight: 72, fontSize: 20 }}
                  onClick={() => setCondition(c.value)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {!conditions && item.hasPhoto && (
          <div style={{ marginTop: 16 }}>
            <button style={bigButton(color.green)} onClick={() => advance(draft)}>Next ▸</button>
          </div>
        )}
      </div>

      <div style={footer}>
        <button style={{ ...button(color.white, color.grey), flex: 1 }} onClick={skipItem}>Skip this item</button>
      </div>
    </div>
  )
}
